import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState, KpiCard } from "@/components/common";
import { formatINR, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Truck, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PERF = { Excellent: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]", Good: "bg-primary/10 text-primary", Average: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]", New: "bg-secondary text-secondary-foreground" };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", payable: 0 });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/suppliers").then((r) => setSuppliers(r.data));
  useEffect(() => { load(); }, []);
  if (!suppliers) return <Loader label="Loading suppliers…" />;

  const totalPayable = suppliers.reduce((s, x) => s + (x.payable || 0), 0);
  const totalPurchased = suppliers.reduce((s, x) => s + (x.total_purchased || 0), 0);

  const add = async () => {
    setSaving(true);
    try { await api.post("/suppliers", { ...form, payable: +form.payable }); toast.success("Supplier added"); setOpen(false); setForm({ name: "", phone: "", email: "", payable: 0 }); load(); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="Purchases, payables and vendor performance.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-supplier-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Supplier</Button></DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">Add Supplier</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="supplier-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1" /></div>
              <div><Label>Amount Payable ₹</Label><Input type="number" value={form.payable} onChange={(e) => setForm((f) => ({ ...f, payable: e.target.value }))} className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={add} disabled={saving || !form.name} data-testid="save-supplier-btn" className="w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard testid="supp-count" index={0} label="Suppliers" value={suppliers.length} icon={Truck} />
        <KpiCard testid="supp-payable" index={1} label="Total Payable" value={formatINR(totalPayable, { compact: true })} icon={Truck} accent="warning" />
        <KpiCard testid="supp-purchased" index={2} label="Total Purchased" value={formatINR(totalPurchased, { compact: true })} icon={Truck} accent="primary" />
      </div>

      <SectionCard testid="suppliers-table">
        {suppliers.length === 0 ? <EmptyState icon={Truck} title="No suppliers" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold py-3">Supplier</th>
                <th className="text-left font-semibold py-3 hidden md:table-cell">Performance</th>
                <th className="text-right font-semibold py-3">Purchased</th>
                <th className="text-right font-semibold py-3">Payable</th>
                <th className="text-left font-semibold py-3 hidden sm:table-cell">Last Payment</th>
              </tr></thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
                    <td className="py-3"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.phone}</div></td>
                    <td className="py-3 hidden md:table-cell"><span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${PERF[s.performance] || PERF.New}`}>{s.performance}</span></td>
                    <td className="py-3 text-right tabular-nums">{formatINR(s.total_purchased, { compact: true })}</td>
                    <td className="py-3 text-right tabular-nums font-semibold text-[hsl(var(--warning))]">{formatINR(s.payable, { compact: true })}</td>
                    <td className="py-3 hidden sm:table-cell text-muted-foreground">{s.last_payment ? timeAgo(s.last_payment) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
